<template>
  <div class="my-assignment-page">
    <div class="uk-grid-medium" uk-grid>
        <PageHeader :title="myAssignmentTitle"
                    class="uk-width-4-4@m"
                    icon="uil-book-open" />
        <div class="uk-width-1-1@s uk-width-3-4@m main-column">
          <div class="section-header pb-0">
            <div class="section-header-left">
              <form action="#">
                <input v-model="searchQuery" :placeholder="$t('education.search')" type="text">
              </form>
            </div>
            <div class="section-header-right ">
              <view-toggle v-model="activeTab" :aria-label="$t('general.view')"
                           :options="viewToggleOptions" @change="activeTab = $event"/>

              <multiple-select-input v-model="filters.order_by"
                                     :allowEmpty="false"
                                     :multiple="false"
                                     :required="false"
                                     :searchable="false"
                                     :showLabels="false"
                                     :values="filterTypes"
                                     class="order-select"
                                     @select="handleOrderChange"/>
            </div>

            <assignment-tabs :active-filters="filters"
                             :counts="tabCounts"
                             :tabs="assignmentTabs"
                             @tab-change="handleTabChange"/>

          </div>

          <!-- Skeleton Loading -->
          <div v-if="isLoading"
               :class="activeTab === 1 ? 'uk-child-width-1-3@m uk-child-width-1-3@s course-card-grid uk-grid-match mt-2' : ''"
               :uk-grid="activeTab === 1 ? '' : null">
            <education-card-skeleton v-for="n in 9" :key="`skeleton-${n}`"
                                     :view-mode="activeTab == 1 ? 'card' : 'list'"></education-card-skeleton>
          </div>

          <!-- Actual Cards -->
          <template v-else>
            <!-- Empty State -->
            <div v-if="showEmptyState" class="empty-assignment-state">
              <div class="empty-assignment-icon">
                <i class="uil-book-open"></i>
              </div>
              <h4 class="empty-assignment-title">{{ $t('general.no_result_found') || 'Veri Bulunamadı' }}</h4>
            </div>

            <!-- Cards List -->
            <div v-else
                 :class="activeTab === 1 ? 'uk-child-width-1-3@m uk-child-width-1-3@s course-card-grid uk-grid-match mt-2' : ''"
                 :uk-grid="activeTab === 1 ? '' : null">
              <education-card v-for="(item, index) in sortedItems"
                              :id="item.id"
                              :key="`${activeTab}-${item.id}-${index}`"
                              :action-label="item.action_label"
                              :bottom-title="item.bottom_title"
                              :edu-item="item"
                              :has-present="item.has_present"
                              :image="item.cover_image_link"
                              :is-assignment-end-date-expired="isExpired(item.end_date)"
                              :is-watchable-again="item.is_watchable_again"
                              :order-number="item.order_number"
                              :process-width="Math.min(item.completion_percentage || 0, 100)"
                              :processClass="getProcessClass(item.completion_percentage)"
                              :rate="item.total_rating"
                              :repeatable="item.repeatable"
                              :sub-title="item.sub_title"
                              :time="formatDate(item.time)"
                              :title="item.name"
                              :view-mode="activeTab == 1 ? 'card' : 'list'"
                              bottom-class=""
                              icon-class="uil-play-circle"
                              links=""
                              @onClickCard="handleCardClick(item.link_type, item.id)"></education-card>
            </div>
            <load-more-button
                :visible="sortedItems.length > 0"
                :has-more="hasMore"
                :loading="loadingMore"
                @load-more="loadMore"
            />
          </template>
        </div>

        <div class="uk-width-1-1@s uk-width-1-4@m sidebar-column">
          <assignment-filter-sidebar :filtered-tags="filteredTags"
                                     :loading="tagsLoading"
                                     :search-tag="searchTag"
                                     :selected-hashtag="filters.selected_hashtag_id"
                                     @search-tag-change="searchTag = $event"
                                     @hashtag-change="handleHashtagChange">
            <template v-if="assignmentWidgetOptions.length > 1" #header>
              <multiple-select-input :value="selectedWidgetOption"
                                     :allowEmpty="false"
                                     :multiple="false"
                                     :required="false"
                                     :showLabels="false"
                                     :searchable="false"
                                     :values="assignmentWidgetOptions"
                                     class="widget-select"
                                     @select="handleWidgetChange"/>
            </template>
          </assignment-filter-sidebar>
        </div>
    </div>
  </div>
</template>

<script>
import EducationCard from "@/view/components/index/EducationCard.vue";
import EducationCardSkeleton from "@/view/components/card/EducationCardSkeleton.vue";
import moment from "moment";
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/az';
import 'moment/locale/tr';
import 'moment/locale/en-gb';
import AssignmentTabs from "./components/AssignmentTabs.vue";
import AssignmentFilterSidebar from "./components/AssignmentFilterSidebar.vue";
import LoadMoreButton from "./components/LoadMoreButton.vue";
import { ASSIGNMENT_TABS_ITEMS } from "@/core/constant/user-ui.constants";
import { GET_COUNTS, GET_ITEMS } from "@/core/services/store/REST.module";
import _ from "lodash";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import ViewToggle from "@/view/components/toggle/ViewToggle.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import axios from "axios";
import SecureLS from "secure-ls";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
const PER_PAGE = 9;

const DASHBOARD_ONLY_WIDGET_TYPES = ["leader_table", "social_wall", "collections"];

// Widget type'a göre filter parametreleri (dashboard.module.js ile aynı)
const WIDGET_FILTER_PARAMS = {
  'upcoming_trainings': {
    up_coming_trainings: true,
    order_by: 'end_date',
    sort: 'asc',
    with_expired: false,
    only_on_progress: false,
  },
  'highlights_trainings': {
    highlight_education: 1,
    order_by: 'created_at',
    order: 'desc',
    with_expired: false,
    only_on_progress: false,
  },
  'most_liked_trainings': {
    only_popular: true,
    order_by: 'created_at',
    order: 'desc',
    with_expired: false,
    only_on_progress: false,
  },
  'expired_trainings': {
    with_expired: true,
    order_by: 'created_at',
    order: 'desc',
    only_on_progress: false,
  },
  'new_trainings': {
    sort: 'desc',
    order: 'id',
    with_expired: false,
  },
  'competency_trainings': {
    only_competencies: true,
    order_by: 'end_date',
    sort: 'asc',
  }
};

export default {
  name: "MyEducationOrAssignment",
  components: {
    MultipleSelectInput,
    EducationCard,
    EducationCardSkeleton,
    AssignmentTabs,
    AssignmentFilterSidebar,
    LoadMoreButton,
    ViewToggle,
    PageHeader
  },
  data() {
    return {
      loadingMore: false,
      filterTypes: [
        { id: 'created_at', name: this.$t('filter.Filter_by_date') },
        { id: 'name', name: this.$t('filter.Filter_by_name') },
      ],
      restUrl: 'api/assignment',
      searchQuery: "",
      activeTab: 1,
      items: { data: [], last_page: 1 },
      searchTag: '',
      pageNumber: 1,
      listLoading: true,
      initialDataFetched: false,
      filters: {
        just_assigned: true,
        selected_hashtag_id: null,
        completed: false,
        on_progress: false,
        repeat_it: false,
        with_expired: false,
        order_by: { id: "created_at", name: this.$t('filter.Filter_by_date') },
        sort: 'desc',
        search: null,
        widget_id: null,
        widget_type: null,
      },
      assigmentCounts: {},
      searchTagList: [],
      tagsLoading: true,
      menuList: [],
      viewToggleOptions: [
        { id: 1, icon: 'icon-feather-grid', tooltip: '' },
        { id: 2, icon: 'icon-feather-list', tooltip: '' }
      ]
    };
  },
  computed: {
    // Store'dan module listesini al
    moduleList() {
      return this.$store.getters['dashboard/moduleList'] || {};
    },

    // Seçili widget'ı bul (widget_id varsa)
    selectedWidget() {
      if (!this.filters.widget_id) return null;
      const dashboard = this.moduleList?.dashboard || [];
      return dashboard.find(widget => widget.id === Number(this.filters.widget_id));
    },

    // Sistem widget tipi (widget_type query veya widget_id üzerinden)
    systemWidgetType() {
      if (this.filters.widget_type && WIDGET_FILTER_PARAMS[this.filters.widget_type]) {
        return this.filters.widget_type;
      }
      if (this.selectedWidget && !this.selectedWidget.widgetable_type && WIDGET_FILTER_PARAMS[this.selectedWidget.type]) {
        return this.selectedWidget.type;
      }
      return null;
    },

    isCustomWidgetView() {
      if (this.selectedWidget?.widgetable_type) return true;
      return Boolean(this.filters.widget_id && !this.systemWidgetType);
    },

    // Dropdown'da "Tümü" mü seçili (hiçbir widget seçili değil)?
    isAllWidgetsView() {
      return !this.filters.widget_id && !this.filters.widget_type;
    },

    // Sekme sayaçları yalnızca "Tümü" (widget seçili değil) görünümünde gösterilir.
    // Widget seçiliyken count API işlevsiz ve liste total'ı o tab'a özel olduğu için sayaç gösterilmez.
    tabCounts() {
      return this.isAllWidgetsView ? this.assigmentCounts : {};
    },

    defaultAssignmentTitle() {
      const sourceMenu = this.getDashboardMenuItems(this.menuList);
      const menuItem = sourceMenu.find(item => item.type === "my-assignment");
      return this.getLocalizedWidgetTitle(menuItem, this.$t("sideNav.My_education"));
    },

    activeDashboardWidgets() {
      return (this.moduleList?.dashboard || []).filter(widget => widget.status === 1);
    },

    assignmentWidgetOptions() {
      // Dil değişince dropdown etiketleri yenilensin
      const locale = this.$i18n?.locale;

      // İlk seçenek "Tümü" -> varsayılan my-assignment görünümüne döner
      const options = [{
        id: "all",
        name: this.$t("general.all"),
        query: {},
      }];

      this.activeDashboardWidgets.forEach(widget => {
        const option = this.toWidgetDropdownOption(widget);
        if (option) options.push(option);
      });

      return options;
    },

    selectedWidgetOption() {
      const { widget_id, widget_type } = this.$route.query;

      if (widget_id) {
        return this.assignmentWidgetOptions.find(
          option => Number(option.query.widget_id) === Number(widget_id)
        ) || null;
      }

      if (widget_type) {
        return this.assignmentWidgetOptions.find(
          option => option.query.widget_type === widget_type
        ) || null;
      }

      return this.assignmentWidgetOptions.find(option => option.id === 'all') || null;
    },

    isAssignmentVisible() {
      const menu = this.menuList || [];
      const assignmentItem = menu.find(item => item.type === "my-assignment");
      return assignmentItem?.status === 1 ?? false;
    },
    myAssignmentTitle() {
      // "Tümü" seçiliyken sayfa başlığı menünün gerçek adını gösterir
      if (!this.selectedWidgetOption || this.selectedWidgetOption.id === "all") {
        return this.defaultAssignmentTitle;
      }
      return this.selectedWidgetOption.name;
    },

    assignmentTabs() {
      return ASSIGNMENT_TABS_ITEMS
    },

    isLoading() {
      return this.listLoading && this.pageNumber === 1
    },

    showEmptyState() {
      return !this.isLoading && this.initialDataFetched && this.sortedItems.length === 0
    },

    hasMore() {
      return this.items.last_page > this.pageNumber
    },

    filteredTags() {
      if (!this.searchTag) return this.searchTagList
      const search = this.searchTag.toLowerCase()
      return this.searchTagList.filter(tag => tag.name.toLowerCase().includes(search))
    },

    sortedItems() {
      const { order_by } = this.filters
      const items = [...this.items.data]

      if (order_by.id === "name") {
        return items.sort((a, b) => a.name.localeCompare(b.name))
      }
      return items.sort((a, b) => a[order_by.id] - b[order_by.id])
    }
  },

  watch: {
    searchQuery: _.debounce(function (val) {
      this.filters.search = val
      this.applyFilter()
    }, 1000),

    '$route.query': {
      handler(newQuery, oldQuery) {
        // Query parametreleri değiştiğinde verileri güncelle
        // Sadece widget_id veya tab değişmişse işlem yap
        if (newQuery.widget_id !== oldQuery?.widget_id ||
            newQuery.widget_type !== oldQuery?.widget_type ||
            newQuery.tab !== oldQuery?.tab) {
          this.handleRouteQueryChange(newQuery)
        }
      },
      deep: true
    }
  },

  methods: {
    isAssignmentListWidget(widget) {
      if (DASHBOARD_ONLY_WIDGET_TYPES.includes(widget.type)) return false;
      if (!widget.widgetable_type && WIDGET_FILTER_PARAMS[widget.type]) return true;
      return Boolean(widget.widgetable_type);
    },

    toWidgetDropdownOption(widget) {
      if (!this.isAssignmentListWidget(widget)) return null;

      if (!widget.widgetable_type && WIDGET_FILTER_PARAMS[widget.type]) {
        const name = this.getLocalizedWidgetTitle(widget);
        return {
          id: `type_${widget.type}`,
          name,
          query: { widget_type: widget.type },
        };
      }

      if (widget.widgetable_type) {
        const name = this.getLocalizedWidgetTitle(widget);
        return {
          id: `widget_${widget.id}`,
          name,
          query: { widget_id: widget.id },
        };
      }

      return null;
    },

    isExpired(date) {
      return moment(date).isBefore(moment())
    },

    formatDate(value) {
      const date = new Date(value)
      return date ? moment(date).locale(this.secureLocalStorage("language")).format('DD MMMM YYYY') : ''
    },

    getProcessClass(percentage) {
      if (percentage >= 75) return ['bg-success']
      if (percentage >= 50) return ['bg-warning']
      return []
    },

    async getFilteredTags() {
      this.tagsLoading = true
      try {
        const { data } = await axios.get('/api/active-tags')
        this.searchTagList = data || []
      } catch (error) {
        console.error('Failed to fetch tags:', error)
        this.searchTagList = []
      } finally {
        this.tagsLoading = false
      }
    },

    async loadMore() {
      if (this.loadingMore || !this.hasMore) return

      this.loadingMore = true
      this.pageNumber++

      try {
        const filters = { ...this.buildFilters(), page: this.pageNumber }

        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.restUrl,
          acceptPromise: false,
          showLoading: false,
          filters
        })

        if (result.status) {
          const formattedItems = result.data.data.map(item => this.formatAssignmentItem(item))
          this.items.data.push(...formattedItems)
          this.items.last_page = result.data.last_page
        }
      } catch (error) {
        console.error("Error loading more items:", error)
      } finally {
        this.loadingMore = false
      }
    },

    handleCardClick(linkType, id) {
      const route = linkType === 1 ? 'user.assignment-detail' : 'user.education-detail'
      this.$router.push({ name: route, params: { id } })
    },

    handleHashtagChange(hashtagId) {
      this.filters.selected_hashtag_id = hashtagId ? Number(hashtagId) : null
      this.applyFilter()
    },

    handleOrderChange(value) {
      this.filters.order_by = value
      this.applyFilter()
    },

    buildFilters() {
      // Her görünümde geçerli olan ortak parametreler (arama, sayfalama, aktif sekme)
      const commonFilters = {
        search: this.filters.search,
        perPage: PER_PAGE,
        by_user: true,
        hashtag_id: this.filters.selected_hashtag_id,
        ...this.tabStatusFilters(),
      }

      // Sistem widget'ı (yaklaşan, öne çıkan vb.) -> dashboard.module.js ile aynı parametreler
      if (this.systemWidgetType) {
        return this.mergeWidgetFilters(commonFilters, WIDGET_FILTER_PARAMS[this.systemWidgetType])
      }

      // Admin'in eklediği özel widget -> yalnızca widget_id ile filtrelenir
      if (this.isCustomWidgetView && this.filters.widget_id) {
        return this.mergeWidgetFilters({ ...commonFilters, widget_id: this.filters.widget_id })
      }

      // Varsayılan "Eğitimlerim" görünümü
      return this.mergeWidgetFilters(commonFilters)
    },

    // Aktif sekmenin (durum) filtreleri
    tabStatusFilters() {
      const { just_assigned, on_progress, completed, repeat_it } = this.filters
      return { just_assigned, on_progress, completed, repeat_it }
    },

    // Kullanıcının seçtiği sıralama (ada göre A-Z, diğerlerinde seçili yön)
    defaultSort() {
      const { order_by, sort } = this.filters
      return {
        order_by: order_by.id,
        order: order_by.id === 'name' ? 'asc' : sort,
      }
    },

    // Ortak filtrelere widget parametrelerini + sıralama ve süre kurallarını ekler
    mergeWidgetFilters(baseFilters, widgetParams = {}) {
      // Widget kendi sıralamasını tanımladıysa onu koru, yoksa kullanıcının seçimini uygula
      const sortFilters = widgetParams.order_by ? {} : this.defaultSort()

      return {
        ...baseFilters,
        ...widgetParams,
        ...sortFilters,
        with_expired: this.resolveWithExpired(widgetParams),
      }
    },

    // "Tamamlananlar" sekmesi süresi dolmuş eğitimleri de kapsar
    resolveWithExpired(widgetParams = {}) {
      if (widgetParams.with_expired !== undefined) return widgetParams.with_expired
      if (this.filters.completed) return false
      return this.filters.with_expired
    },

    async applyFilter() {
      this.pageNumber = 1
      const filters = { ...this.buildFilters(), page: 1 }
      this.listLoading = true
      this.initialDataFetched = false

      try {
        const result = await this.$store.dispatch(GET_ITEMS, {
          url: this.restUrl,
          acceptPromise: false,
          filters,
          showLoading: true
        })

        if (result.status) {
          this.items = {
            data: result.data.data.map(item => this.formatAssignmentItem(item)),
            last_page: result.data.last_page
          }
        }
      } catch (error) {
        console.error('Filter failed:', error)
      } finally {
        this.listLoading = false
        this.initialDataFetched = true
      }
    },

    setActiveTab(activeKey = null) {
      const tabKeys = ['just_assigned', 'on_progress', 'completed', 'repeat_it']
      tabKeys.forEach(key => {
        this.filters[key] = key === activeKey
      })
    },

    handleTabChange({ key, value }) {
      const newQuery = {
        ...this.$route.query,
        tab: key,
        val: value
      }
      if (this.$route.query.tab !== key || this.$route.query.val !== String(value)) {
        this.$router.replace({ name: this.$route.name, query: newQuery })
      }
    },

    handleWidgetChange(option) {
      if (!option || this.selectedWidgetOption?.id === option.id) return;

      const newQuery = { ...this.$route.query };
      delete newQuery.widget_id;
      delete newQuery.widget_type;

      if (option.query.widget_id) {
        newQuery.widget_id = option.query.widget_id;
      }
      if (option.query.widget_type) {
        newQuery.widget_type = option.query.widget_type;
      }

      this.$router.replace({ name: this.$route.name, query: newQuery });
    },

    formatAssignmentItem(item) {
      return {
        ...item,
        description: item.description || "-",
        time: item.start_date,
        bottom_title: this.getBottomTitle(item.completion_percentage),
        sub_title: item.is_compulsory ? this.$t('assignment.compulsory') : this.$t('assignment.not_compulsory'),
        link_type: 1,
        action_label: item.action_label || null,
      }
    },

    getBottomTitle(percentage) {
      if (percentage === 0) return this.$t('general.start')
      if (percentage >= 100) return this.$t('general.detail')
      return this.$t('general.continue')
    },

    async getAssignmentCounts() {
      try {
        const result = await this.$store.dispatch(GET_COUNTS, {
          url: `${this.restUrl}/counts`,
          acceptPromise: false,
          showLoading: true
        })
        this.assigmentCounts = result.data
      } catch (error) {
        console.error('Failed to fetch counts:', error)
      }
    },

    handleRouteQueryChange(query) {
      const { tab, widget_id, widget_type } = query

      this.filters.widget_id = widget_id || null
      this.filters.widget_type = widget_type || null

      // tab yoksa (ör. başka sayfadan butonla gelindiğinde) varsayılan "Tümü" sekmesi
      if (tab) {
        this.setActiveTab(tab === 'with_expired' ? null : tab)
      } else {
        this.setActiveTab(null)
      }

      this.applyFilter()
    },

    async init() {
      const data = ls.get('dashboardMenuList')
      if (data?.menu) this.menuList = data.menu

      // Check if assignment menu is visible
      if (!this.isAssignmentVisible) {
        this.$router.push({ path: "/dashboard" });
        return
      }

      // Module listesi yüklenmemişse yükle (widget parametrelerini doğru kullanabilmek için)
      if (!this.moduleList || Object.keys(this.moduleList).length === 0) {
        await this.$store.dispatch('dashboard/fetchModuleList', this.authUser.company_id)
      }

      this.getAssignmentCounts()
      this.getFilteredTags()

      // Handle query params - URL'deki tab ve widget_id parametrelerini al
      this.handleRouteQueryChange(this.$route.query)
    }
  },

  mounted() {
    this.init()
  }
}
</script>

<style scoped src="./my-assignments.scoped.css"></style>
